'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import { FeedbackFormFields, FeedbackFormProps } from './types';
import OptimizedImage from '../../optimizedImage/ui/OptimizedImage';

import IconTelegramSquare from '@/assets/icons/iconTelegramSquare.svg';
import IconWhatsAppSquare from '@/assets/icons/iconWhatsAppSquare.svg';
import sendLeadFormData from '@/shared/api/sendLeadFormData';
import { Button } from '@/shared/components/button';
import { Dropdown } from '@/shared/components/dropdown/ui/Dropdown';
import { FeedbackFormSuccess } from '@/shared/components/feedbackFormSuccess';
import { Input } from '@/shared/components/input';
import { PhoneInput } from '@/shared/components/phoneInput';
import { Textarea } from '@/shared/components/textarea';
import { LOCALES } from '@/shared/constants/common';
import { Field } from '@/shared/constants/formsFields';
import { getCountriesForPhoneInput } from '@/shared/constants/phoneCodes';
import { useUTMParams } from '@/shared/hooks';
import { sendAnalyticsEvent } from '@/shared/lib/analytics/analytics';
import { ANALYTICS_EVENTS } from '@/shared/lib/analytics/analyticsEvents';
import { classNames } from '@/shared/lib/classNames/classNames';

const getGoogleAnalyticsClientId = (): string | null => {
  try {
    const gaCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_ga='));

    if (!gaCookie) {
      return null;
    }

    const gaValue = gaCookie.split('=')[1];

    const parts = gaValue.split('.');

    if (parts.length < 4) {
      return null;
    }

    const clientId = `${parts[2]}.${parts[3]}`;

    return clientId;
  } catch (error) {
    return null;
  }
};

export const FeedbackForm = ({
  translations,
  fields,
  className,
  locale,
  images,
}: FeedbackFormProps) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    resetField,
    formState: { errors },
  } = useForm<FeedbackFormFields>();
  const ruLocale = locale === LOCALES.ru;
  const kzLocale = locale === LOCALES.kz;
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);

  const { getUTMParams } = useUTMParams();

  const onSubmit = (data: FeedbackFormFields) => {
    const sanitizedData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value),
    );
    const utmParams = getUTMParams();
    const clientId = getGoogleAnalyticsClientId();

    const formDataWithUtm = {
      ...(sanitizedData as FeedbackFormFields),
      ...utmParams,
      ...(clientId && { clientId }),
      submitDate: new Date().toISOString().slice(0, 19),
      referer: process.env.NEXT_PUBLIC_BASE_URL || 'https://dev.2gis.ru',
    };

    sendLeadFormData(formDataWithUtm)
      .then((response) => {
        if (response?.meta)
          toast.error(translations.fieldsFormError, {
            style: {
              background: 'var(--surface-base-secondary)',
              color: 'var(--text-base-primary)',
            },
          });
        else {
          setIsSuccessPopupVisible(true);
          reset();
          sendAnalyticsEvent(ANALYTICS_EVENTS.SUBMIT_FEEDBACK_FORM);
        }
      })
      .catch(() =>
        toast.error(translations.fieldsFormError, {
          style: {
            background: 'var(--surface-base-secondary)',
            color: 'var(--text-base-primary)',
          },
        }),
      );
  };

  useEffect(
    () => Object.keys(getValues()).forEach((field) => resetField(field)),
    [fields],
  );

  const getField = ({
    name,
    required,
    className,
    label,
    type,
    options,
    value,
  }: Field) => {
    switch (type) {
      case 'textarea':
        return (
          <Textarea
            key={name}
            label={translations[label]}
            className={className}
            watch={watch}
            {...register(name)}
          />
        );
      case 'dropdown':
        return (
          <Dropdown
            key={name}
            className={className}
            label={translations[label]}
            error={errors[name]}
            watch={watch}
            options={options!
              .map(({ label, ...rest }) => ({
                label: translations[label],
                ...rest,
              }))
              .filter(({ label }) => label)}
            {...register(name, {
              required: required && translations[required],
            })}
            onChange={(value: number | string) =>
              setValue(name, value as string)
            }
          />
        );
      case 'phone':
        return (
          <PhoneInput
            key={name}
            className={className}
            label={translations[label]}
            error={errors[name]}
            watch={watch}
            countries={getCountriesForPhoneInput(locale, images.flags)}
            onPhoneChange={(value: string) => setValue('phone', value)}
            {...register(name, {
              required: required && translations[required],
            })}
          />
        );
      default:
        return (
          <Input
            key={name}
            className={className}
            label={translations[label]}
            error={errors[name]}
            watch={watch}
            {...register(name, {
              required: required && translations[required],
              value: value,
            })}
          />
        );
    }
  };

  return (
    <>
      <form
        className="rounded-20 bg-surface-base-inverse-primary relative px-12 py-10 max-sm:rounded-[20px] max-sm:px-5 max-sm:py-6"
        onSubmit={handleSubmit(onSubmit)}
        id="form"
      >
        <p
          className={classNames({
            cls: 'text-[44px] leading-[52px] font-medium tracking-[-1%] max-sm:text-[24px] max-sm:leading-[130%]',
            additional: [className],
          })}
        >
          {translations.formTitle}
        </p>
        <section className="mt-10 mb-2 max-w-[calc(100%-362px)] max-lg:max-w-full max-sm:mt-5">
          <OptimizedImage
            src={images.logo}
            alt="logo"
            className="absolute top-[141px] right-[92px] h-auto w-[248px] max-lg:hidden"
            width={248}
            height={248}
          />

          <div className="mb-5 grid grid-cols-2 gap-5 max-sm:mb-3 max-sm:grid-cols-1 max-sm:gap-3">
            {fields.map((field) => getField(field))}
          </div>
          <div className="mt-3 flex items-center gap-[26px] max-sm:mt-1 max-sm:flex-col max-sm:items-start max-sm:gap-3">
            <Button
              type="submit"
              className="flex h-14 w-[216px] items-center justify-center whitespace-nowrap max-sm:w-full"
              variant="fourth"
              ariaLabel={translations.submitButtonLabel}
            >
              {translations.submitButtonLabel}
            </Button>
            <div
              className="text-text-base-secondary max-w-[486px] text-[12px] leading-[16px]"
              dangerouslySetInnerHTML={{
                __html: translations.submitButtonDisclaimer,
              }}
            />
          </div>
        </section>
      </form>

      <section
        id="contacts"
        className="bg-surface-base-secondary mt-7 flex flex-wrap items-center justify-between rounded-[28px] px-10 py-10 max-sm:h-auto max-sm:flex-col max-sm:items-start max-sm:rounded-none max-sm:bg-transparent max-sm:px-0 max-sm:pb-0 sm:gap-2 xl:px-[137px]"
      >
        <div className="flex flex-col">
          <div className="relative top-[-5px] mb-1 flex gap-2 text-white/20 opacity-65">
            <Link
              href={`${translations.contactsSocialLinksTelegram}`}
              aria-label="telegram"
            >
              <IconTelegramSquare />
            </Link>
            <Link
              href={`${translations.contactsSocialLinksWhatsapp}`}
              aria-label="whatsapp"
            >
              <IconWhatsAppSquare />
            </Link>
          </div>
          {ruLocale || kzLocale ? (
            <Link
              href={`tel:${translations.contactsPhoneAll?.replace(/[\s-]/g, '')}`}
              className="text-[20px] leading-[32px]"
              aria-label={translations.contactsPhoneAll}
            >
              {translations.contactsPhoneAll}
            </Link>
          ) : (
            <Link
              href={`mailto:${translations.contactsEmail}`}
              className="text-[20px] leading-[32px]"
              aria-label={translations.contactsEmail}
            >
              {translations.contactsEmail}
            </Link>
          )}
        </div>

        <div className="flex flex-col max-sm:mt-6">
          <span className="text-text-base-secondary mb-3 text-[16px] leading-[24px] max-sm:mb-1">
            {translations.contactsPhoneText}
          </span>
          <Link
            href={`tel:${translations.contactsPhoneLocale?.replace(/[\s-]/g, '')}`}
            className="text-[20px] leading-[32px]"
            aria-label={translations.contactsPhoneLocale}
          >
            {translations.contactsPhoneLocale}
          </Link>
        </div>
        <div className="flex flex-col max-sm:mt-6">
          <Link
            href={`${translations.contactsOfficeMapLink}`}
            aria-label={translations.contactsOfficeMapLink}
            className="text-text-base-secondary mb-3 text-[16px] leading-[24px] max-sm:mb-1"
          >
            {translations.contactsOfficeMapText}
          </Link>

          {(ruLocale || kzLocale) && (
            <Link
              href={`mailto:${translations.contactsEmail}`}
              className="text-[20px] leading-[32px]"
              aria-label={translations.contactsEmail}
            >
              {translations.contactsEmail}
            </Link>
          )}
        </div>
      </section>

      {isSuccessPopupVisible && (
        <FeedbackFormSuccess
          onClose={() => setIsSuccessPopupVisible(false)}
          translations={translations}
          locale={locale}
        />
      )}
    </>
  );
};
